import { __assign, __spreadArrays } from "tslib";
import { createPredicate, getEventPath, isNumber, isObject, ItemManager, matchesPredicate } from '@tyler-components-web/core';
import { MultiSortManager } from './multi-sort-manager';
import { TABLE_CONSTANTS } from './table-constants';
import { TableRow } from './table-row';
import { TableUtils } from './table-utils';
import { SortDirection, CellAlign } from './types';
/**
 * The state class behind the `TableComponent` custom element.
 */
var TableFoundation = /** @class */ (function () {
    function TableFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        // Backing model values
        this._tableRows = [];
        this._data = [];
        this._columnConfigurations = [];
        this._select = false;
        this._multiselect = true;
        this._dense = false;
        this._roomy = false;
        this._filter = false;
        this._fixedHeaders = false;
        this._layoutType = TABLE_CONSTANTS.strings.DEFAULT_LAYOUT_TYPE;
        this._wrapContent = true;
        this._resizable = false;
        this._minResizeWidth = TABLE_CONSTANTS.numbers.MIN_RESIZE_WIDTH;
        this._multiColumnSort = false;
        this._selectCheckboxAlignment = CellAlign.Center;
        // State variables
        this._isAllSelected = false;
        this._sortedColumnIndex = -1;
        this._rendered = false;
        this._allowRowClick = false;
        this._multiSortManager = new MultiSortManager();
        // Create all listeners
        this._rowClickListener = function (evt) { return _this._onRowClick(evt); };
        this._rowDoubleClickListener = function (evt) { return _this._onRowDoubleClick(evt); };
        this._selectRowListener = function (evt) { return _this._onRowSelected(evt); };
        this._selectAllListener = function (evt) { return _this._onSelectAll(evt); };
        this._headRowMouseDownListener = function (evt) { return _this._onHeadRowMouseDown(evt); };
        this._headRowContextMenuListener = function (evt) { return _this._onHeadRowContextMenu(evt); };
        this._documentMouseMoveListener = function (evt) { return _this._onMouseMove(evt); };
        this._documentMouseUpListener = function (evt) { return _this._onMouseUp(evt); };
        this._filterListener = function (value, columnIndex) { return _this._onFilter(value, columnIndex); };
        // Create helpers
        this._selectionManager = new ItemManager(this._selectKey);
        this._hiddenColumnManager = new ItemManager();
    }
    /** Called when the component is connected to the DOM. */
    TableFoundation.prototype.initialize = function () {
        this.connect();
        this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.SELECT, this._select.toString());
        this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.MULTISELECT, this._multiselect.toString());
        this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.DENSE, this._dense.toString());
        this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.ROOMY, this._roomy.toString());
        this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.FILTER, this._filter.toString());
        this._adapter.emitHostEvent(TABLE_CONSTANTS.events.INITIALIZED, true, false);
    };
    TableFoundation.prototype.connect = function () {
        this._adapter.initialize();
    };
    TableFoundation.prototype.disconnect = function () {
        this._resizeDescriptor = undefined;
        this._adapter.removeDocumentListener('mousemove', this._documentMouseMoveListener);
        this._adapter.removeDocumentListener('mouseup', this._documentMouseUpListener);
    };
    Object.defineProperty(TableFoundation.prototype, "data", {
        get: function () {
            return JSON.parse(JSON.stringify(this._data));
        },
        /** The data to be displayed in the table body. */
        set: function (value) {
            var _this = this;
            if (Array.isArray(value) && value.length > 0) {
                if (isObject(value[0])) {
                    this._data = value.map(function (v) { return (__assign({}, v)); });
                }
                else {
                    this._data = __spreadArrays(value);
                }
            }
            else {
                this._data = [];
            }
            this._tableRows = this._data.map(function (data) { return new TableRow(data, _this._selectionManager.exists(data)); });
            this._renderBody();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "columnConfigurations", {
        get: function () {
            // Intentional shallow copy of member properties. These member objects have properties that are references to functions.
            //   The typical JSON.parse(JSON.stringify(object)) will not work here. If this becomes an issue we'll add a deepClone
            //   function to the core library.
            return this._columnConfigurations.map(function (cc) { return (__assign({}, cc)); });
        },
        /** The column configuration options. */
        set: function (value) {
            var _this = this;
            // Intentional shallow copy of member properties. These member objects have properties that are references to functions.
            //   The typical JSON.parse(JSON.stringify(object)) will not work here. If this becomes an issue we'll add a deepClone
            //   function to the core library.
            this._columnConfigurations = value.map(function (cc) { return (__assign({}, cc)); });
            // Update hidden column manager
            this._hiddenColumnManager.clear();
            this._columnConfigurations.forEach(function (cc) {
                if (cc.hidden) {
                    _this._hiddenColumnManager.add(cc);
                }
            });
            this._refreshTableSort(); // Must be called after updating the hidden columns
            this.render();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "_visibleColumnConfigurations", {
        /** Gets the visible column configurations. */
        get: function () {
            var _this = this;
            return this._columnConfigurations.filter(function (cc) { return !_this._hiddenColumnManager.exists(cc); });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "_tableConfiguration", {
        /** Gets the table configuration. */
        get: function () {
            return {
                tableElement: this._adapter.getTableElement(),
                columnConfigurations: this._visibleColumnConfigurations,
                data: this._tableRows,
                clickListener: this._allowRowClick ? this._rowClickListener : null,
                doubleClickListener: this._allowRowClick ? this._rowDoubleClickListener : null,
                selectListener: this._select ? this._selectRowListener : null,
                selectAllListener: this._multiselect ? this._selectAllListener : null,
                headRowMouseDownListener: this._headRowMouseDownListener,
                headRowContextMenuListener: this._headRowContextMenuListener,
                filterListener: this._filter ? this._filterListener : null,
                sortDirection: this._sortDirection,
                sortedColumn: this._sortedColumnIndex,
                multiColumnSort: this._multiColumnSort,
                filter: this._filter,
                wrapContent: this._wrapContent,
                fixedHeaders: this._fixedHeaders,
                layoutType: this._layoutType,
                dense: this._dense,
                resizable: this._resizable,
                rowCreatedCallback: this._rowCreatedCallback,
                cellCreatedCallback: this._cellCreatedCallback,
                selectAllTemplate: this._selectAllTemplate,
                selectCheckboxAlignment: this._selectCheckboxAlignment,
                tooltipSelect: this._tooltipSelect,
                tooltipSelectAll: this._tooltipSelectAll
            };
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "select", {
        get: function () {
            return this._select;
        },
        /** Controls the visibility of the select column. */
        set: function (value) {
            if (this._select !== value) {
                this._select = value;
                // Now we can add/remove the column
                this._adapter.setSelectColumnVisibility(this._adapter.getTableElement(), this._select, this._selectRowListener, this._multiselect ? this._selectAllListener : undefined, this._multiselect ? this._selectAllTemplate : null, this._selectCheckboxAlignment, this._tableRows, this._tooltipSelect, this._tooltipSelectAll);
                this._updateSelections(true);
                // Update the attribute on the host element
                if (this._select) {
                    this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.SELECT, this._select.toString());
                }
                else {
                    this._adapter.removeHostAttribute(TABLE_CONSTANTS.attributes.SELECT);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "multiselect", {
        get: function () {
            return this._multiselect;
        },
        /** Controls the visibility of the select all checkbox. */
        set: function (value) {
            if (this._multiselect !== value) {
                this._multiselect = value;
                if (this._select) {
                    this._adapter.setSelectAllVisibility(this._adapter.getTableElement(), this._multiselect, this._multiselect ? this._selectAllListener : null, this._multiselect ? this.selectAllTemplate : null, this._selectCheckboxAlignment, this._tooltipSelectAll);
                    this._updateSelections(true);
                }
                this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.MULTISELECT, this._multiselect.toString());
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "selectKey", {
        get: function () {
            if (Array.isArray(this._selectKey)) {
                return __spreadArrays(this._selectKey);
            }
            return this._selectKey;
        },
        /** The row key for matching data to selections. */
        set: function (value) {
            if (this._originalSelectKey !== value) {
                if (Array.isArray(value)) {
                    this._originalSelectKey = __spreadArrays(value);
                    this._selectKey = __spreadArrays(value);
                }
                else {
                    this._originalSelectKey = value;
                    this._selectKey = [value];
                }
                this._selectionManager.setKey(this._selectKey);
                this._updateSelections(false);
                // Update the attribute on the host element
                if (typeof this._originalSelectKey === 'string') {
                    this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.SELECT_KEY, this._originalSelectKey);
                }
                else {
                    this._adapter.removeHostAttribute(TABLE_CONSTANTS.attributes.SELECT_KEY);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "dense", {
        /** Controls whether the table is dense or not. False by default. */
        get: function () {
            return this._dense;
        },
        set: function (value) {
            if (this._dense !== value) {
                this._dense = value;
                this._adapter.setDense(this._adapter.getTableElement(), this._dense);
                this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.DENSE, this._dense.toString());
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "roomy", {
        get: function () {
            return this._roomy;
        },
        set: function (value) {
            if (value !== this._roomy) {
                this._roomy = value;
                this._applyRoomy();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "filter", {
        get: function () {
            return this._filter;
        },
        /** Controls whether the table filters are visible or not. */
        set: function (value) {
            if (this._filter !== value) {
                this._filter = value;
                this._adapter.setFilterRow(this._tableConfiguration);
                this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.FILTER, this._filter.toString());
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "fixedHeaders", {
        get: function () {
            return this._fixedHeaders;
        },
        /** Controls whether the table has fixed headers or not. */
        set: function (value) {
            if (this._fixedHeaders !== value) {
                this._fixedHeaders = value;
                this._adapter.setFixedHeaders(this._tableConfiguration);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "layoutType", {
        /** Controls the table layout algorithm */
        get: function () {
            return this._layoutType;
        },
        set: function (value) {
            if (this._layoutType !== value) {
                this._layoutType = value;
                this._adapter.setTableLayoutType(this._tableConfiguration);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "wrapContent", {
        /** Controls whether the content in each cell wraps or not */
        get: function () {
            return this._wrapContent;
        },
        set: function (value) {
            if (this._wrapContent !== value) {
                this._wrapContent = value;
                this._adapter.setWrapContentState(this._tableConfiguration);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "resizable", {
        /** Controls whether the columns are resizable or not. */
        get: function () {
            return this._resizable;
        },
        set: function (value) {
            if (this._resizable !== value) {
                this._resizable = value;
                this._adapter.setResizable(this._tableConfiguration);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "minResizeWidth", {
        /** Gets/sets the minimum width that a column can be resized to by the user dragging the resize handle. */
        get: function () {
            return this._minResizeWidth;
        },
        set: function (value) {
            if (this._minResizeWidth !== value) {
                this._minResizeWidth = value;
                if (!isNumber(this._minResizeWidth)) {
                    this._minResizeWidth = TABLE_CONSTANTS.numbers.MIN_RESIZE_WIDTH;
                }
                else if (this._minResizeWidth < 0) {
                    this._minResizeWidth = 0;
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "allowRowClick", {
        /** Gets/sets whether the rows respond to click events or not. */
        get: function () {
            return this._allowRowClick;
        },
        set: function (value) {
            if (this._allowRowClick !== value) {
                this._allowRowClick = value;
                if (this._rendered) {
                    this._adapter.setRowClickListeners(this._adapter.getTableElement(), this._allowRowClick, this._rowClickListener, this._rowDoubleClickListener);
                    this._adapter.setRowClickAttributes(this._adapter.getTableElement(), this._allowRowClick);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "multiColumnSort", {
        /** Gets/sets whether the table can sort multiple columns. */
        get: function () {
            return this._multiColumnSort;
        },
        set: function (value) {
            if (value !== this._multiColumnSort) {
                this._multiColumnSort = value;
                this._refreshTableSort();
                this.render();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "selectCheckboxAlignment", {
        get: function () {
            return this._selectCheckboxAlignment;
        },
        set: function (value) {
            if (value !== this._selectCheckboxAlignment) {
                this._selectCheckboxAlignment = value;
                this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.SELECT_CHECKBOX_ALIGNMENT, value);
                this.render();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "tooltipSelect", {
        get: function () {
            return this._tooltipSelect;
        },
        set: function (value) {
            if (value !== this._tooltipSelect) {
                this._tooltipSelect = value;
                if (typeof (value) === 'string') {
                    this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.TOOLTIP_SELECT, value);
                }
                this.render();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "tooltipSelectAll", {
        get: function () {
            return this._tooltipSelectAll;
        },
        set: function (value) {
            if (value !== this._tooltipSelectAll) {
                this._tooltipSelectAll = value;
                this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.TOOLTIP_SELECT_ALL, value);
                this.render();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "selectAllTemplate", {
        get: function () {
            return this._selectAllTemplate;
        },
        set: function (value) {
            if (value !== this._selectAllTemplate) {
                this._selectAllTemplate = value;
                this.render();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "rowCreated", {
        set: function (value) {
            this._rowCreatedCallback = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TableFoundation.prototype, "cellCreated", {
        set: function (value) {
            this._cellCreatedCallback = value;
        },
        enumerable: true,
        configurable: true
    });
    TableFoundation.prototype.isRowSelected = function (rowData) {
        var pred = createPredicate(Array.isArray(this._selectKey) ? this._selectKey : [this._selectKey], rowData);
        return this.selectKey ? !!this._selectionManager.getItems().find(function (item) { return matchesPredicate(item, pred); }) : this._selectionManager.exists(rowData);
    };
    /** Returns the selected row instances. */
    TableFoundation.prototype.getSelectedRows = function () {
        return this._selectionManager.getItems();
    };
    /**
     * Sets the selected items in the table.
     * @param {any[]} data The data to select.
     * @param {boolean} clearCurrentSelections True to clear the current selections before setting the new selections.
     */
    TableFoundation.prototype.selectRows = function (data, clearCurrentSelections) {
        if (!this._selectKey) {
            throw new Error('You must set a selectKey to select rows.');
        }
        if (!this._multiselect || clearCurrentSelections) {
            this.clearSelections();
        }
        if (data && data.length) {
            this._selectionManager.add(data);
            this._updateSelections(!clearCurrentSelections);
        }
    };
    /**
     * Deselects rows from in the table data.
     * @param {any[]} data The array of items to deselect.
     */
    TableFoundation.prototype.deselectRows = function (data) {
        if (data && data.length) {
            this._selectionManager.remove(data);
            this._updateSelections(false);
        }
    };
    /**
     * Clears all selections from the table.
     */
    TableFoundation.prototype.clearSelections = function () {
        var tableElement = this._adapter.getTableElement();
        this._selectionManager.clear();
        this._tableRows.forEach(function (tableRow) { return (tableRow.selected = false); });
        this._isAllSelected = false;
        this._adapter.clearSelectedRows(tableElement);
        if (this._select && this._multiselect) {
            this._adapter.updateSelectAllState(tableElement, false);
        }
    };
    /**
     * Hides a table column.
     * @param {number} columnIndex The index of the column to hide.
     */
    TableFoundation.prototype.hideColumn = function (columnIndex) {
        var columnConfig = this._columnConfigurations[columnIndex];
        if (this._sortedColumnIndex === columnIndex) {
            this._sortedColumnIndex = -1;
        }
        if (!this._hiddenColumnManager.exists(columnConfig)) {
            this._hiddenColumnManager.add(columnConfig);
            this.render();
        }
    };
    /**
     * Shows a column that is currently hidden.
     * @param {number} columnIndex The index of the column to show.
     */
    TableFoundation.prototype.showColumn = function (columnIndex) {
        var columnConfig = this._columnConfigurations[columnIndex];
        if (this._hiddenColumnManager.exists(columnConfig)) {
            this._hiddenColumnManager.remove(columnConfig);
            this.render();
        }
    };
    /**
     * Creates and renders the table with the current column configuration and data.
     */
    TableFoundation.prototype.render = function () {
        this._adapter.createTable(this._tableConfiguration);
        this._renderSelections();
        this._rendered = true;
    };
    /**
     * Renders the table body only.
     */
    TableFoundation.prototype._renderBody = function () {
        if (!this._rendered) {
            return;
        }
        this._adapter.recreateTableBody(this._tableConfiguration);
        this._renderSelections();
    };
    TableFoundation.prototype._renderSelections = function () {
        if (this.select) {
            this._isAllSelected = this._getSelectAllState();
            if (this._multiselect) {
                this._adapter.updateSelectAllState(this._adapter.getTableElement(), this._isAllSelected, this._isIndeterminate(this._isAllSelected));
            }
        }
        var selections = this._selectionManager.getItems();
        if (selections.length) {
            this._adapter.setSelectedRows(this._adapter.getTableElement(), this._selectKey, this._data, selections, false);
        }
    };
    /**
     * Expands a collapsed row.
     * @param rowIndex The index of the row to expand.
     */
    TableFoundation.prototype.expandRow = function (rowIndex, template) {
        if (!this._rendered) {
            throw new Error('Cannot expand a row before the table has rendered.');
        }
        if (rowIndex < 0 || rowIndex > this._tableRows.length) {
            throw new Error("Invalid row index: " + rowIndex + ".");
        }
        return this._adapter.expandRow(this._tableConfiguration, rowIndex, template);
    };
    /**
     * Collapses an expanded row.
     * @param rowIndex The index of the row to expand.
     */
    TableFoundation.prototype.collapseRow = function (rowIndex) {
        if (!this._rendered) {
            return Promise.resolve();
        }
        if (rowIndex < 0 || rowIndex > this._tableRows.length) {
            return Promise.resolve();
        }
        return this._adapter.collapseRow(this._tableConfiguration, rowIndex);
    };
    /**
     * Check if a row is expanded or not.
     * @param rowIndex The row index to check.
     */
    TableFoundation.prototype.isRowExpanded = function (rowIndex) {
        return this._adapter.isRowExpanded(this._tableConfiguration, rowIndex);
    };
    /**
     * Selects the rows by the index or indexes.
     * @param indexes The row indexes to select
     * @param preserveExisting Whether or not the existing selections should be preserved
     */
    TableFoundation.prototype.selectRowsByIndex = function (indexes, preserveExisting) {
        var _this = this;
        var rowsToSelect = indexes.map(function (i) { return _this._data[i]; }).filter(function (obj) { return !!obj; });
        this.selectRows(rowsToSelect, !preserveExisting);
    };
    /**
     * Selects the rows by the index or indexes.
     * @param indexes The row indexes to deselect
     */
    TableFoundation.prototype.deselectRowsByIndex = function (indexes) {
        var _this = this;
        var rowsToDeselect = indexes.map(function (i) { return _this._data[i]; }).filter(function (obj) { return !!obj; });
        this.deselectRows(rowsToDeselect);
    };
    /**
     * Determines if every row is selected or not.
     */
    TableFoundation.prototype._getSelectAllState = function () {
        return this._tableRows.length > 0 && this._tableRows.every(function (tr) { return tr.selected; });
    };
    /**
     * Updates the internal selection state as well as UI state.
     * @param {boolean} preserveExisting Should existing selections be preserved when updating selection state.
     */
    TableFoundation.prototype._updateSelections = function (preserveExisting) {
        var _this = this;
        this._tableRows.forEach(function (tableRow) { return (tableRow.selected = _this._selectionManager.exists(tableRow.data)); });
        var isAllSelected = this._getSelectAllState();
        this._adapter.setSelectedRows(this._adapter.getTableElement(), this._selectKey, this._data, this._selectionManager.getItems(), preserveExisting);
        if (isAllSelected !== this._isAllSelected || this._isIndeterminate(isAllSelected)) {
            this._isAllSelected = isAllSelected;
            // (derek.moss): while writting test it seemed like this could never be false.
            // maybe an unneeded check
            if (this._multiselect) {
                this._adapter.updateSelectAllState(this._adapter.getTableElement(), this._isAllSelected, this._isIndeterminate(this._isAllSelected));
            }
        }
    };
    /**
     * Removes all selections from the table.
     */
    TableFoundation.prototype._removeSelections = function () {
        var tableElement = this._adapter.getTableElement();
        // Update our internal state
        this._isAllSelected = false;
        this._selectionManager.clear();
        this._tableRows.forEach(function (item) { return (item.selected = false); });
        // Update the UI to reflect our new state
        this._adapter.clearSelectedRows(tableElement);
        this._adapter.updateSelectAllState(tableElement, false);
    };
    /** Handles a row being single clicked. */
    TableFoundation.prototype._onRowClick = function (evt) {
        evt.stopImmediatePropagation();
        // Ignore if the element being clicked is the checkbox element
        if (evt.target.tagName === 'INPUT' && evt.target.type === 'checkbox') {
            return;
        }
        var composedPath = getEventPath(evt);
        var composedElements = composedPath.filter(function (node) { return node.nodeType === 1; });
        var customCellTemplateElement = composedElements.find(function (el) { return el.hasAttribute(TABLE_CONSTANTS.attributes.CUSTOM_CELL_TEMPLATE); });
        var stopClickPropagation = customCellTemplateElement && !customCellTemplateElement.hasAttribute(TABLE_CONSTANTS.attributes.CUSTOM_CELL_TEMPLATE_PROPAGATE);
        if (stopClickPropagation) {
            return; // We ignore click events that bubble from custom templates, unless configured to allow
        }
        var rowSelectedInfo = this._getSelectedRowFromEvent(evt);
        var data = {
            index: rowSelectedInfo.index,
            data: rowSelectedInfo.data
        };
        this._adapter.emitHostEvent(TABLE_CONSTANTS.events.ROW_CLICK, data, true, false);
    };
    /** Handles a row being double clicked. */
    TableFoundation.prototype._onRowDoubleClick = function (evt) {
        evt.stopPropagation();
        // Ignore if the element being clicked is the checkbox element
        if (evt.target.tagName === 'INPUT' && evt.target.type === 'checkbox') {
            return;
        }
        var rowSelectedInfo = this._getSelectedRowFromEvent(evt);
        var detail = {
            index: rowSelectedInfo.index,
            data: rowSelectedInfo.data
        };
        this._adapter.emitHostEvent(TABLE_CONSTANTS.events.SELECT_DOUBLE, detail, true, false);
    };
    /**
     * Handles a row being selected/deselected.
     */
    TableFoundation.prototype._onRowSelected = function (evt) {
        var _this = this;
        var rowSelectedInfo = this._getSelectedRowFromEvent(evt);
        // Deselect the previously selected row(s) when not in multiselect mode (only if it's different than the newly selected row)
        if (!this._multiselect) {
            var existingSelections = this._tableRows.filter(function (item) { return item.selected; });
            existingSelections.forEach(function (selection) {
                if (!matchesPredicate(selection.data, createPredicate(_this._selectKey, rowSelectedInfo.data))) {
                    _this.deselectRows([selection.data]);
                }
            });
        }
        // Determine what the new selected state should be for this row (opposite of current state)
        var isSelected = !this._selectionManager.exists(rowSelectedInfo.data);
        // Update the table row based on the new selected state
        this._adapter.updateSelectedState(rowSelectedInfo.rowElement, isSelected);
        // Update the selection manager
        isSelected ? this._selectionManager.add(rowSelectedInfo.data) : this._selectionManager.remove(rowSelectedInfo.data);
        // Update the state for this row
        this._tableRows[rowSelectedInfo.index].selected = isSelected;
        // The indexes that we need to emit select events for
        var selectionIndexes = [rowSelectedInfo.index];
        var isAllSelected = false;
        // If we have multiselect on, then we need to update the select all state
        if (this._multiselect) {
            var shift = evt.shiftKey;
            if (shift && this._previouslyClickedRow) {
                selectionIndexes = this._shiftSelectRows(rowSelectedInfo);
                if (selectionIndexes.includes(this._previouslyClickedRow.index)) {
                    var idx = selectionIndexes.indexOf(this._previouslyClickedRow.index);
                    selectionIndexes.splice(idx, 1);
                }
            }
            // Update the state of the select all checkbox
            isAllSelected = this._getSelectAllState();
            this._adapter.updateSelectAllState(this._adapter.getTableElement(), isAllSelected, this._isIndeterminate(isAllSelected));
        }
        // Emit a table row selection changed event
        for (var _i = 0, selectionIndexes_1 = selectionIndexes; _i < selectionIndexes_1.length; _i++) {
            var index = selectionIndexes_1[_i];
            var data = this._tableRows[index].data;
            var selected = this._selectionManager.exists(data);
            var detail = { selected: selected, index: index, data: data };
            this._adapter.emitHostEvent(TABLE_CONSTANTS.events.SELECT, detail, true, false);
        }
        // If selecting/deselecting this row causes the select all state to be changed, then emit the select all changed event.
        if (this._isAllSelected !== isAllSelected) {
            this._isAllSelected = isAllSelected;
            var data = {
                status: this._isAllSelected,
                count: this._tableRows.filter(function (row) { return row.selected; }).length,
                data: __spreadArrays(this._data),
                targetType: 'row'
            };
            this._adapter.emitHostEvent(TABLE_CONSTANTS.events.SELECT_ALL, data, true, false);
        }
        this._previouslyClickedRow = __assign({}, rowSelectedInfo);
    };
    /**
     * Determines which row was chosen from the given event.
     * @param {Event} evt The click event.
     */
    TableFoundation.prototype._getSelectedRowFromEvent = function (evt) {
        var composedPath = getEventPath(evt);
        var rowElement = composedPath.find(function (el) { return el.tagName === 'TR'; });
        var index = this._getNonExpandedRows().findIndex(function (el) { return el === rowElement; });
        var data = this._tableRows[index].data;
        return { rowElement: rowElement, index: index, data: data };
    };
    TableFoundation.prototype._getNonExpandedRows = function () {
        var tableElement = this._adapter.getTableElement();
        return Array.from(tableElement.tBodies[0].rows)
            .filter(function (row) { return !row.classList.contains(TABLE_CONSTANTS.classes.TABLE_ROW_EXPANDABLE_CONTENT); });
    };
    /**
     * Handles the select all checkbox being checked/unchecked.
     */
    TableFoundation.prototype._onSelectAll = function (evt) {
        var _this = this;
        evt.stopPropagation();
        // Toggle the status of our select all state
        this._isAllSelected = !this._isAllSelected;
        var tableElement = this._adapter.getTableElement();
        // Update the select all checkbox in the table based on the new state
        this._adapter.updateSelectAllState(tableElement, this._isAllSelected);
        // Select/deselect every row based on our new select all state
        this._tableRows.forEach(function (tableRow, index) {
            // Update the state for each row
            tableRow.selected = _this._isAllSelected;
            // Update the selection manager
            _this._isAllSelected ? _this._selectionManager.add(tableRow.data) : _this._selectionManager.remove(tableRow.data);
            // Update the table row selected state
            var rowElement = TableUtils.getRowByIndex(tableElement, index);
            if (rowElement) {
                _this._adapter.updateSelectedState(rowElement, _this._isAllSelected);
            }
        });
        // Emit the select all changed event
        var data = {
            status: this._isAllSelected,
            count: this._tableRows.filter(function (row) { return row.selected; }).length,
            data: __spreadArrays(this._data),
            targetType: 'select-all'
        };
        this._adapter.emitHostEvent(TABLE_CONSTANTS.events.SELECT_ALL, data, true, false);
    };
    TableFoundation.prototype._onHeadRowContextMenu = function (evt) {
        evt.preventDefault();
    };
    TableFoundation.prototype._onHeadRowMouseDown = function (evt) {
        if (evt.button !== 0) {
            return;
        }
        var composedPath = getEventPath(evt);
        var target = evt.target;
        // Find the row and cell elements that the event originated within
        var rowElement = composedPath.find(function (el) { return el.tagName === 'TR'; });
        var thElement = composedPath.find(function (el) { return el.tagName === 'TH'; });
        var cellIndex = Array.from(rowElement.cells).findIndex(function (c) { return c === thElement; });
        this._headCellMouseDownIndex = cellIndex;
        if (target !== thElement && target.classList.contains(TABLE_CONSTANTS.classes.TABLE_RESIZE_HANDLE)) {
            this._resizeDescriptor = {
                element: thElement,
                startWidth: thElement.offsetWidth,
                startX: evt.pageX,
                width: thElement.offsetWidth,
                index: this._select ? cellIndex - 1 : cellIndex
            };
            this._adapter.addDocumentListener('mousemove', this._documentMouseMoveListener);
            this._adapter.setResizeColumnVisibility(this._tableConfiguration, cellIndex, true);
        }
        this._adapter.addDocumentListener('mouseup', this._documentMouseUpListener);
    };
    TableFoundation.prototype._onMouseMove = function (evt) {
        if (this._resizeDescriptor) {
            var newWidth = this._resizeDescriptor.startWidth + (evt.pageX - this._resizeDescriptor.startX);
            // Ensure that the user can't drag the column past the minimum column width
            if (newWidth < this._minResizeWidth) {
                newWidth = this._minResizeWidth;
            }
            this._resizeDescriptor.width = newWidth;
            this._resizeDescriptor.element.style.width = newWidth + "px";
            this._adapter.addTableClass(TABLE_CONSTANTS.classes.TABLE_RESIZING);
        }
    };
    TableFoundation.prototype._onMouseUp = function (evt) {
        this._adapter.removeDocumentListener('mousemove', this._documentMouseMoveListener);
        this._adapter.removeDocumentListener('mouseup', this._documentMouseUpListener);
        this._adapter.removeTableClass(TABLE_CONSTANTS.classes.TABLE_RESIZING);
        if (this._headCellMouseDownIndex === undefined) {
            return;
        }
        if (this._resizeDescriptor) {
            this._adapter.normalizeColumnWidths(this._tableConfiguration);
            this._adapter.setResizeColumnVisibility(this._tableConfiguration, this._headCellMouseDownIndex, false);
            var resizeEventData = {
                index: this._resizeDescriptor.index,
                width: this._resizeDescriptor.width
            };
            this._adapter.emitHostEvent(TABLE_CONSTANTS.events.COLUMN_RESIZE, resizeEventData);
            this._resizeDescriptor = undefined;
        }
        else if (this._tableConfiguration.tableElement.tHead.contains(evt.target)) {
            if (this._multiColumnSort) {
                this._onMultiSort(this._headCellMouseDownIndex, evt.ctrlKey);
            }
            else {
                this._onSort(this._headCellMouseDownIndex);
            }
        }
        this._headCellMouseDownIndex = undefined;
    };
    /**
     * Called when a click event is triggered on the table header row.
     * We use this to capture all click events on the row, and determine which
     * header cell was clicked.
     */
    TableFoundation.prototype._onSort = function (cellIndex) {
        // Get the column index so we can access the config for that column (this depends on whether there is a select column or not)
        var columnIndex = this._select ? cellIndex - 1 : cellIndex;
        // Get the column config
        var columnConfig = columnIndex >= 0 ? this._visibleColumnConfigurations[columnIndex] : null;
        // If this column was not sortable, or was the select column then we just return
        if (!columnConfig || !columnConfig.sortable) {
            return;
        }
        // Always default to ascending sort on a newly sorted column
        var desiredSortDirection = SortDirection.Ascending;
        // Toggle the sort direction class
        var isTogglingSortOnSameColumn = columnIndex === this._sortedColumnIndex;
        var currentSortDirectionIsAscending = this._sortDirection === SortDirection.Ascending || this._sortDirection === undefined;
        var isUnsorting = isTogglingSortOnSameColumn && this._sortDirection === SortDirection.Descending;
        if (isTogglingSortOnSameColumn && currentSortDirectionIsAscending) {
            desiredSortDirection = SortDirection.Descending;
        }
        if (isUnsorting && !columnConfig.preventUnsort) {
            desiredSortDirection = SortDirection.Unset;
        }
        // Emit the sort event for this column
        var data = {
            direction: desiredSortDirection,
            columnIndex: columnIndex
        };
        if (!this._adapter.emitHostEvent(TABLE_CONSTANTS.events.SORT, data, true, true)) {
            // If defaultPrevented is true, we emit the event without updating internal state.
            return;
        }
        if (isTogglingSortOnSameColumn && this._sortDirection === SortDirection.Descending && !columnConfig.preventUnsort) {
            this._adapter.removeColumnSort(this._adapter.getTableElement(), this._select ? this._sortedColumnIndex + 1 : this._sortedColumnIndex);
            this._sortedColumnIndex = -1;
            return;
        }
        // Update internal state
        this._sortDirection = desiredSortDirection;
        // Toggle the sort direction class
        if (columnIndex === this._sortedColumnIndex) {
            // Update the table with the new sort direction
            this._adapter.setSortDirection(this._adapter.getTableElement(), cellIndex, this._sortDirection);
        }
        else {
            // If we have a column this is currently sorted, remove the sort from that column
            if (this._sortedColumnIndex >= 0) {
                this._adapter.removeColumnSort(this._adapter.getTableElement(), this._select ? this._sortedColumnIndex + 1 : this._sortedColumnIndex);
            }
            // Set this column as the sorted column
            this._sortedColumnIndex = columnIndex;
            // Set the new sort direction on the newly sorted column
            this._adapter.setSortedColumn(this._adapter.getTableElement(), cellIndex, this._sortDirection);
        }
    };
    TableFoundation.prototype._onMultiSort = function (cellIndex, ctrlKey) {
        // Get the column index so we can access the config for that column (this depends on whether there is a select column or not)
        var columnIndex = this._select ? cellIndex - 1 : cellIndex;
        // Get the column config
        var columnConfig = columnIndex >= 0 ? this._visibleColumnConfigurations[columnIndex] : null;
        // If this column was not sortable, or was the select column then we just return
        if (!columnConfig || !columnConfig.sortable) {
            return;
        }
        var revertColumns = this._multiSortManager.sortedColumns;
        var sortColumn = this._multiSortManager.getSortColumn(columnIndex);
        if (sortColumn) {
            this._multiSortManager.updateSortColumn(sortColumn);
        }
        else if (ctrlKey) {
            this._multiSortManager.addSortColumn({ direction: SortDirection.Descending, columnIndex: columnIndex, propertyName: columnConfig.property });
        }
        else {
            this._multiSortManager.clearMultiSort();
            this._multiSortManager.addSortColumn({ direction: SortDirection.Descending, columnIndex: columnIndex, propertyName: columnConfig.property });
        }
        var data = this._multiSortManager.sortedColumns.map(function (col) { return (__assign({}, col)); });
        if (!this._adapter.emitHostEvent(TABLE_CONSTANTS.events.SORT, data, true, true)) {
            // If defaultPrevented is true, we emit the event without updating internal state.
            this._multiSortManager.setSortColumns(revertColumns);
            return;
        }
        var newSortedColumns = this._multiSortManager.sortedColumns;
        var hasMultipleColumnsSorted = newSortedColumns.length > 1;
        // clear any unsorted columns in the UI
        for (var virtualIndex = 0; virtualIndex < this._visibleColumnConfigurations.length; virtualIndex++) {
            // actualIndex takes into account if there is a select column, virtualIndex is 0 -> visible data columns length
            var actualIndex = this._select ? virtualIndex + 1 : virtualIndex;
            if (!this._multiSortManager.isSortColumn(virtualIndex)) {
                this._adapter.removeColumnSort(this._adapter.getTableElement(), actualIndex);
                TableUtils.setMultiSortColumnSortNumber(this._adapter.getTableElement(), hasMultipleColumnsSorted, actualIndex, null);
            }
            else {
                var column = this._multiSortManager.getSortColumn(virtualIndex);
                if (column) {
                    this._adapter.setSortedColumn(this._adapter.getTableElement(), actualIndex, column.direction);
                    TableUtils.setMultiSortColumnSortNumber(this._adapter.getTableElement(), hasMultipleColumnsSorted, actualIndex, column.sortOrder || null);
                }
            }
        }
    };
    /**
     * Handles filter events and emits the filter information event.
     * @param evt The event.
     */
    TableFoundation.prototype._onFilter = function (value, columnIndex) {
        var data = { value: value, columnIndex: columnIndex };
        this._adapter.emitHostEvent(TABLE_CONSTANTS.events.FILTER, data, true, false);
    };
    TableFoundation.prototype._shiftSelectRows = function (selectedRow) {
        var _this = this;
        var indexes = [];
        if (this._previouslyClickedRow) {
            var prevIndex = this._previouslyClickedRow.index;
            var currIndex = selectedRow.index;
            var startIndex = currIndex >= prevIndex ? prevIndex : currIndex;
            var endIndex = currIndex >= prevIndex ? currIndex : prevIndex;
            endIndex += endIndex > startIndex ? 1 : 0;
            startIndex += startIndex > endIndex ? 1 : 0;
            var action = function (data) { return _this.selectRows(data, false); };
            var existingSelection = this._tableRows.find(function (item) { return item.selected && item.data === selectedRow.data; });
            var shouldDeselect = this._tableRows.find(function (item) { return item.data === _this._previouslyClickedRow.data && !item.selected; });
            // If you are clicking a checkbox that is already selected, deselect all the rows
            if (!existingSelection || shouldDeselect) {
                action = function (data) { return _this.deselectRows(data); };
            }
            for (var i = startIndex; i < endIndex; i++) {
                indexes.push(i);
            }
            action(this.data.filter(function (v, i) { return indexes.includes(i); }));
        }
        return indexes;
    };
    TableFoundation.prototype._isIndeterminate = function (isAllSelected) {
        return !isAllSelected && this.getSelectedRows().length > 0 && this._data.length !== this.getSelectedRows().length;
    };
    TableFoundation.prototype._applyRoomy = function () {
        this._adapter.setRoomy(this._adapter.getTableElement(), this._roomy);
        this._adapter.setHostAttribute(TABLE_CONSTANTS.attributes.ROOMY, this._roomy.toString());
    };
    TableFoundation.prototype._refreshTableSort = function () {
        this._sortedColumnIndex = this._visibleColumnConfigurations.findIndex(function (c) { return c.initialSort; });
        this._sortDirection = this._sortedColumnIndex >= 0 ? this._visibleColumnConfigurations[this._sortedColumnIndex].sortDirection : undefined;
        if (this._multiColumnSort) {
            var sortedColumns_1 = [];
            this._visibleColumnConfigurations.forEach(function (col, index) {
                if (typeof col.initialSort === 'object') {
                    sortedColumns_1.push({
                        columnIndex: index,
                        direction: col.initialSort.direction,
                        sortOrder: col.initialSort.sortOrder,
                        propertyName: col.property
                    });
                }
            });
            this._multiSortManager.setSortColumns(sortedColumns_1);
        }
    };
    return TableFoundation;
}());
export { TableFoundation };
